import * as React from "react";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { matchesSearchTokens } from "@/lib/search-utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface ComboboxOption {
  value: string;
  label: string;
  subtitle?: string;
  group?: string;
}

interface EntityComboboxProps {
  options: ComboboxOption[];
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  createLabel?: string;
  disabled?: boolean;
  allowClear?: boolean;
  getVisibleOptions?: (
    options: ComboboxOption[],
    search: string,
  ) => ComboboxOption[];
  keepOpenOnSelect?: boolean;
  testId?: string;
  renderCreateForm?: (props: {
    onSuccess: (newId: string) => void;
    onCancel: () => void;
    initialName?: string;
  }) => React.ReactNode;
}

export function EntityCombobox({
  options,
  value,
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  createLabel = "Create new",
  disabled = false,
  allowClear = true,
  keepOpenOnSelect = false,
  getVisibleOptions,
  testId,
  renderCreateForm,
}: EntityComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = getVisibleOptions
    ? getVisibleOptions(options, search)
    : options.filter((opt) => matchesSearchTokens(opt.label, search));

  const handleSelect = (optionValue: string) => {
    if (optionValue === value) {
      if (allowClear) {
        onChange(null);
      }
    } else {
      onChange(optionValue);
    }
    if (!keepOpenOnSelect) {
      setOpen(false);
    }
    setSearch("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const handleCreateClick = () => {
    setOpen(false);
    setCreateDialogOpen(true);
  };

  const handleCreateSuccess = (newId: string) => {
    onChange(newId);
    setCreateDialogOpen(false);
    setSearch("");
  };

  const handleCreateCancel = () => {
    setCreateDialogOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            data-testid={testId}
            disabled={disabled}
            className={cn(
              "w-full justify-between font-normal",
              !selectedOption && "text-muted-foreground",
            )}
          >
            <span className="truncate">
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {allowClear && selectedOption && (
                <span
                  role="button"
                  className="h-4 w-4 opacity-50 hover:opacity-100 cursor-pointer"
                  onClick={handleClear}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <X className="h-4 w-4" />
                </span>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0 bg-popover z-50"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              data-testid={testId ? `${testId}-search` : undefined}
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup>
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    data-testid={
                      testId ? `${testId}-option-${option.value}` : undefined
                    }
                    onSelect={() => handleSelect(option.value)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option.value ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div className="flex flex-col">
                      <span>{option.label}</span>
                      {option.subtitle && (
                        <span className="text-xs text-muted-foreground">
                          {option.subtitle}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
              {renderCreateForm && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={handleCreateClick}
                      className="cursor-pointer text-primary"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {createLabel}
                      {search && ` "${search}"`}
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {renderCreateForm && (
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{createLabel}</DialogTitle>
            </DialogHeader>
            {renderCreateForm({
              onSuccess: handleCreateSuccess,
              onCancel: handleCreateCancel,
              initialName: search,
            })}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
